import Pagination from "@/components/Pagination";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Profile from "@/redux/models/Profile";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { handleNotAllowedError } from "@/utils/error";
import { OrderActions } from "./OrderActions";
import LoadingSpinner from "../LoadingSpinner";
import useLogout from "@/hooks/useLogout";
import ItemDesignDisplay from "../ItemDesignDisplay/ItemDesignDisplay";

const perPage = 10;

export const Orders = () => {
  const orders = useSelector(Profile.selectors.getOrdersList);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(orders.length / perPage);
  const { status } = useSession();
  const isLogged = status === "authenticated";
  const logout = useLogout();

  const statusColor = {
    approved: "bg-red-100 border-red-400 text-red-500",
    canceled: "bg-red-100 border-red-400 text-red-500",
    shipped: "bg-green-50 border-green-400 text-green-500",
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      Profile.types.getOrders({
        finallyCb: () => setLoading(false),
        catchCb: (err) => {
          if ([401, 403].includes(err?.response?.status)) {
            logout();
          }
          handleNotAllowedError(err, router, isLogged);
        },
      })
    );
  }, []);

  return (
    <div className="w-full relative">
      {loading && (
        <div className="absolute z-50 flex w-full justify-center text-center h-full pt-36 bg-opacity-40 bg-slate-50">
          <LoadingSpinner className="text-blue-500 h-10 w-10" />
        </div>
      )}
      {!orders.length && !loading ? (
        "No Orders found"
      ) : (
        <div className="w-full">
          <section>
            <div className="hidden lg:grid lg:grid-cols-[1fr_2fr_1fr_2fr_1fr_1fr_1fr_1fr] w-full text-xs capitalize text-gray-900 font-semibold underline">
              <div className="flex items-center justify-center w-full text-center"></div>
              <div className="flex items-center justify-center w-full text-center">Order</div>
              <div className="flex items-center justify-center w-full text-center">Total</div>
              <div className="flex items-center justify-center w-full text-center">Status</div>
              <div className="flex items-center justify-center w-full text-center">Created</div>
              <div className="flex items-center justify-center w-full text-center whitespace-nowrap">
                Est. Ship
              </div>
              <div className="flex items-center justify-center w-full text-center">Ship</div>
              <div>Actions</div>
            </div>
          </section>
          <section className="w-full px-2">
            {orders?.slice((page - 1) * perPage, (page - 1) * perPage + perPage)?.map((order) => (
              <div
                key={order.id}
                className="relative grid grid-cols-2 lg:grid-cols-[1fr_2fr_1fr_2fr_1fr_1fr_1fr_1fr] w-full text-xs capitalize py-4 border-b space-y-1 lg:space-y-0"
              >
                <div className="flex flex-col items-center justify-center w-full col-span-2 mb-5 space-y-3 text-center lg:col-span-1 lg:mb-0">
                  {order?.items?.map((item) => (
                    <div key={item?.id}>
                      <ItemDesignDisplay item={item} imageClassName="h-10 w-10" size="small" />
                    </div>
                  ))}
                </div>
                <div className="flex w-full lg:justify-center items-center text-center text-[0.65rem]">
                  <span className="block mr-1 font-bold lg:hidden text-xs">Order:</span>
                  {order.id}
                </div>
                <div className="flex items-center w-full text-center lg:justify-center">
                  <span className="block mr-2 font-bold lg:hidden">Total:</span>$
                  {parseFloat(order?.pricing?.total, 10).toFixed(2)}
                </div>
                <div className="flex items-center w-full text-center lg:justify-center">
                  <span className="block mr-2 font-bold lg:hidden">Status:</span>
                  <div
                    className={`flex items-center justify-center rounded-full py-1 lg:px-3 px-1 border ${
                      statusColor[order.status] || "bg-orange-50 border-orange-400 text-orange-500"
                    }`}
                  >
                    {order.status.replace(/_/g, " ").replace("approved", "unpaid")}
                  </div>
                </div>
                <div className="flex items-center w-full text-center lg:justify-center">
                  <span className="block mr-2 font-bold lg:hidden">Created:</span>
                  {order.created_date ? dayjs(order.created_date).format("MM/DD/YYYY") : "-"}
                </div>
                <div className="flex items-center w-full text-center lg:justify-center">
                  <span className="block mr-2 font-bold lg:hidden">Est. Ship:</span>
                  {order.estimated_shipping_date
                    ? dayjs(order.estimated_shipping_date).format("MM/DD/YYYY")
                    : "-"}
                </div>
                <div className="flex items-center w-full text-center lg:justify-center">
                  <span className="block mr-2 font-bold lg:hidden">Ship:</span>
                  {order.shipped_date ? dayjs(order.shipped_date).format("MM/DD/YYYY") : "-"}
                </div>
                <OrderActions order={order} />
              </div>
            ))}
          </section>

          <section className="flex items-center justify-center w-full py-2">
            {totalPages > 1 && (
              <Pagination
                page={page}
                total={totalPages}
                disableNext={page === totalPages}
                disablePrev={page === 1}
                handleNext={() => setPage(page + 1)}
                handlePrev={() => setPage(page - 1)}
              />
            )}
          </section>
        </div>
      )}
    </div>
  );
};
